/**
 * The button.
 *
 * Four intents, deliberately few: `primary` is the one action a screen is
 * about, `default` is everything else, `quiet` is an action that should not
 * compete with the content beside it, and `danger` is destructive. A screen
 * with two primary buttons has no primary button.
 *
 * Busy state is built in rather than bolted on, because every slow action in
 * this app goes through a button and the old arrangement — a helper that
 * swapped the label text and put a spinner in a pseudo-element — could not
 * survive an icon-only button and had to special-case it. Here the label stays
 * put, the spinner replaces the icon slot, and the width does not jump.
 *
 * And it is now automatic: a handler that returns a promise puts its own button
 * into that state until the promise settles. Thirty-five buttons across
 * fourteen views started async work and showed nothing while it ran — Refresh,
 * Sync, Copy link, every rename and every delete — so pressing one looked
 * exactly like missing it, and the reader pressed again. Each of those could
 * have been given a `useState` by hand; all of them being right, and staying
 * right, is a property of this file instead.
 *
 * The `busy` prop still works and still wins: an action whose waiting is
 * managed somewhere else — a form that submits, a queue that drains — says so
 * explicitly.
 */

import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Button as HeadlessButton } from "@headlessui/react";
import { Spinner } from "./Spinner.jsx";
import styles from "./Button.module.css";

/* How long the work has to run before the button admits to it, and how long it
   then keeps saying so. Both exist because the first version of this had
   neither: Refresh on the feed was measured going busy and back in *four
   milliseconds* against a local server, which draws a spinner nobody can see
   and is indistinguishable from the nothing it was meant to replace.

   So: work that finishes almost at once shows nothing, because the result
   arriving is its own feedback and a flicker is just noise. Work that outlasts
   the first threshold gets a spinner that stays put long enough to read. The
   button is disabled from the first moment either way — that part is about
   what a second click would do, not about what anybody can see. */
const ADMIT_AFTER_MS = 120;
const STAY_FOR_MS = 320;

export const Button = forwardRef(function Button(
  {
    intent = "default",
    size = "md",
    icon: Icon,
    iconEnd: IconEnd,
    busy = false,
    disabled = false,
    full = false,
    className = "",
    onClick,
    children,
    ...rest
  },
  ref,
) {
  const iconOnly = !children;
  /* Two, not one: `running` is whether a second click would start the work
     again, and `showing` is whether anybody has been told. They part company
     for the first tenth of a second of every action. */
  const [running, setRunning] = useState(false);
  const [showing, setShowing] = useState(false);

  /* Because the work a button starts is frequently the work that removes it:
     confirming a delete unmounts the row the button was on, and settling the
     promise afterwards would set state on nothing. */
  const alive = useRef(true);
  const timers = useRef([]);
  useEffect(
    () => () => {
      alive.current = false;
      timers.current.forEach(clearTimeout);
    },
    [],
  );

  const later = useCallback((run, delay) => {
    const timer = setTimeout(() => {
      if (alive.current) run();
    }, delay);
    timers.current.push(timer);
    return timer;
  }, []);

  const press = useCallback(
    (event) => {
      const started = onClick?.(event);
      if (!started || typeof started.then !== "function") return started;

      setRunning(true);
      let admitted = 0;
      const admit = later(() => {
        admitted = Date.now();
        setShowing(true);
      }, ADMIT_AFTER_MS);

      return started.finally(() => {
        clearTimeout(admit);
        if (!alive.current) return;
        // Nothing was ever shown, so there is nothing to leave up.
        const left = admitted ? STAY_FOR_MS - (Date.now() - admitted) : 0;
        const settle = () => {
          setRunning(false);
          setShowing(false);
        };
        if (left > 0) later(settle, left);
        else settle();
      });
    },
    [onClick, later],
  );

  // Told explicitly, a button says so at once: the caller managing the wait has
  // already decided this is worth showing.
  const waiting = busy || showing;
  const locked = disabled || busy || running;

  return (
    <HeadlessButton
      ref={ref}
      // Busy is a kind of disabled: the work is already running, and a second
      // click would start it again. Saying so in `aria-busy` as well means a
      // screen reader announces the wait rather than a dead control.
      disabled={locked}
      aria-busy={waiting || undefined}
      onClick={press}
      data-intent={intent}
      data-size={size}
      data-icon-only={iconOnly || undefined}
      className={`${styles.button} ${full ? styles.full : ""} ${className}`.trim()}
      {...rest}
    >
      {waiting ? (
        <Spinner className={styles.icon} />
      ) : (
        Icon && <Icon className={styles.icon} aria-hidden="true" />
      )}
      {children && <span className={styles.label}>{children}</span>}
      {IconEnd && !waiting && <IconEnd className={styles.icon} aria-hidden="true" />}
    </HeadlessButton>
  );
});
